import { useMemo } from 'react';
import type { IMoodRecord } from '@shared/types';
import { getMoodUI } from '../../lib/moodUtils/getMoodUIDetails';
import {
  formatMonthKey,
  groupMoodRecordsByMonth,
} from '../../lib/moodUtils/groupMoodRecordsByMonth';
import { clx } from '../../lib/utils';

interface IMoodCalendarProps {
  isLoading: boolean;
  moodRecords: IMoodRecord[];
}

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const MoodCalendar = ({ moodRecords, isLoading }: IMoodCalendarProps) => {
  const groupedRecords = useMemo(() => groupMoodRecordsByMonth(moodRecords), [moodRecords]);

  const monthKeys = useMemo(() => {
    return Object.keys(groupedRecords).sort((a, b) => b.localeCompare(a));
  }, [groupedRecords]);

  if (isLoading) {
    // @TODO: show skeleton.
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-8 p-4">
      {monthKeys.map((monthKey) => (
        <MonthCalendar
          key={monthKey}
          monthKey={monthKey}
          records={groupedRecords[monthKey] || []}
        />
      ))}
    </div>
  );
};

interface IMonthCalendarProps {
  monthKey: string;
  records: IMoodRecord[];
}

const MonthCalendar = ({ monthKey, records }: IMonthCalendarProps) => {
  const monthName = formatMonthKey(monthKey);
  const [year, month] = monthKey.split('-').map(Number);

  const days = useMemo(() => {
    if (!year || !month) {
      return [];
    }

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const firstDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const daysArray: Array<{ day: number; moodRecord?: IMoodRecord }> = [];

    // Add empty cells for days before the first day of the month.
    for (let i = 0; i < firstDayOfWeek; i++) {
      daysArray.push({ day: 0 });
    }

    // Create a map of mood records by day.
    const recordsByDay = records.reduce(
      (acc, record) => {
        const day = parseInt(record.mood_date.split('-')[2] || '0', 10);
        acc[day] = record;
        return acc;
      },
      {} as Record<number, IMoodRecord>
    );

    // Add all days of the month.
    for (let day = 1; day <= totalDays; day++) {
      daysArray.push({ day, moodRecord: recordsByDay[day] });
    }

    return daysArray;
  }, [year, month, records]);

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{monthName}</h2>
      <div className="grid grid-cols-7 gap-2">
        {DAYS_OF_WEEK.map((day, index) => (
          <div key={index} className="text-center text-xs font-medium text-gray-600">
            {day}
          </div>
        ))}
        {days.map(({ day, moodRecord }, index) => {
          if (day === 0) {
            return <div key={index} className="aspect-square" />;
          }

          const bgColor = moodRecord ? getMoodUI(moodRecord.label_mood).bgColor : 'bg-white';

          return (
            <button
              key={index}
              className={clx(
                bgColor,
                'aspect-square rounded-full border-2 border-black text-sm font-medium'
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};
